import React, { useMemo, useState } from "react";

import Grid from "@mui/material/Grid";
import { useCustomerPortalJobContext } from "Context/CustomerPortalJobContext";
import {
  ClipboardTick as ClipboardTickIcon,
  Profile2User as Profile2UserIcon,
} from "iconsax-react";
import { UserCustomerPortal } from "Models/customer.portal";
import { useTranslation } from "react-i18next";
import { Metric, handleGetConvertValue } from "Util/converts";
import { getMeasurement, getWeightMeasurement } from "Util/getMeasurement";

import { CustomerPortalCard } from "../../../../CustomerPortalCard";
import { CustomerPortalListItem } from "../../../../CustomerPortalListItem";
import { SummaryInventoriesDetailsSection } from "../SummaryInventoriesDetailsSection";
import { SummaryCheckListModal } from "./SummaryCheckListModal";
import { SummaryContactSectionModal } from "./SummaryContactSectionModal";
import { SummaryJobDetailsSectionCard } from "./SummaryJobDetailsSectionCard";

enum MovingOption {
  FROM = "movingFrom",
  TO = "movingTo",
}
type SectionModal = MovingOption.FROM | MovingOption.TO;
type ModalOpened = SectionModal | false;

const getInfoUser = (userCustomerPortal?: Partial<UserCustomerPortal>) => {
  const allInfos = [];

  if (userCustomerPortal?.fullName) allInfos.push(userCustomerPortal?.fullName);
  if (userCustomerPortal?.phone) allInfos.push(userCustomerPortal?.phone);

  if (userCustomerPortal?.email) allInfos.push(userCustomerPortal?.email);

  if (!allInfos.length) return "-";

  return allInfos.join(" | ");
};

export const SummaryJobDetailsSection = () => {
  const { order, isLoading } = useCustomerPortalJobContext();
  const measurement = getMeasurement();
  const weightMeasurement = getWeightMeasurement();
  const { t: translate } = useTranslation();
  const [isModalOpen, setModalOpen] = useState<ModalOpened>(false);
  const [isContactModalOpened, setIsContactModalOpened] = useState<ModalOpened>(false);

  const volume = useMemo(() => {
    return `${handleGetConvertValue(
      order?.inventories?.actual.totalVolume || 0,
      measurement?.code,
      measurement?.decimalPlaces
    )} ${measurement?.code || Metric["m³"]}`.trim();
  }, [order, measurement]);

  const weight = useMemo(() => {
    return `${handleGetConvertValue(
      order?.inventories?.actual.totalWeight || 0,
      weightMeasurement?.code,
      weightMeasurement?.decimalPlaces
    )} ${weightMeasurement?.code || Metric.kg}`.trim();
  }, [order, weightMeasurement]);

  const movingFromAdress = useMemo(() => {
    if (!order) return [];

    return order.addresses.origins.map((address) => address.longAddress!).filter(Boolean);
  }, [order]);

  const movingToAdress = useMemo(() => {
    if (!order) return [];

    return order.addresses.destinations.map((address) => address.longAddress!).filter(Boolean);
  }, [order]);

  const data = useMemo(() => {
    return {
      movingFrom: {
        title: translate("pickupAccessDetails"),
        getTitle: (values: { [x in SectionModal]: string }, key: MovingOption) => values[key],
        access: order?.accesses.pickup,
        addresses: order?.addresses.origins,
        contacts: order?.contacts?.origin || [],
      },
      movingTo: {
        title: translate("deliveryAccessDetails"),
        getTitle: (values: { [x: string]: string }, key: SectionModal) => values[key],
        access: order?.accesses.delivery,
        addresses: order?.addresses.destinations,
        contacts: order?.contacts?.destination || [],
      },
    };
  }, [order]);

  const currentData = useMemo(() => {
    if (!isModalOpen && !isContactModalOpened) return;

    return data[(isModalOpen || isContactModalOpened) as keyof typeof data];
  }, [data, isModalOpen, isContactModalOpened]);

  return (
    <CustomerPortalCard
      title={translate("orderDetails")}
      block
      fullHeight
      justifyContent="space-between"
    >
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <CustomerPortalListItem
            title={translate("actualAsQuoted")}
            subtitle={`${translate("volume")} ${volume} | ${translate("weight")} ${weight}`}
            isLoadingSubtitle={isLoading}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <CustomerPortalListItem
            title={translate("moveCoordinator")}
            subtitle={getInfoUser(order?.moveCoordinator)}
            isLoadingSubtitle={isLoading}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <SummaryJobDetailsSectionCard
            title={translate("movingFrom")}
            actions={[
              {
                label: translate("seeAccessChecklist"),
                Icon: ClipboardTickIcon,
                onClick: () => setModalOpen(MovingOption.FROM),
              },
              {
                label: `${translate("originContacts")} (${order?.contacts?.origin?.length ?? 0})`,
                Icon: Profile2UserIcon,
                onClick: order?.contacts?.origin?.length
                  ? () => setIsContactModalOpened(MovingOption.FROM)
                  : undefined,
              },
            ]}
            items={movingFromAdress}
            isLoading={isLoading}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <SummaryJobDetailsSectionCard
            title={translate("movingTo")}
            actions={[
              {
                label: translate("seeAccessChecklist"),
                Icon: ClipboardTickIcon,
                onClick: () => setModalOpen(MovingOption.TO),
              },
              {
                label: `${translate("destinationContacts")} (${
                  order?.contacts?.destination?.length ?? 0
                })`,
                Icon: Profile2UserIcon,
                onClick: order?.contacts?.destination?.length
                  ? () => setIsContactModalOpened(MovingOption.TO)
                  : undefined,
              },
            ]}
            items={movingToAdress}
            isLoading={isLoading}
          />
        </Grid>
      </Grid>

      <SummaryCheckListModal
        open={!!isModalOpen}
        onClose={() => {
          setModalOpen(false);
        }}
        title={currentData?.title}
        access={currentData?.access}
        addresses={currentData?.addresses}
      />
      <SummaryContactSectionModal
        open={!!isContactModalOpened}
        onClose={() => {
          setIsContactModalOpened(false);
        }}
        title={
          isContactModalOpened
            ? currentData?.getTitle(
                {
                  [MovingOption.FROM]: translate("originContacts"),
                  [MovingOption.TO]: translate("destinationContacts"),
                },
                isContactModalOpened
              )
            : undefined
        }
        contacts={currentData?.contacts || []}
      />
      <SummaryInventoriesDetailsSection />
    </CustomerPortalCard>
  );
};
